import type { GoogleFontsIndexResponse } from '@jobilla/nexus-sdk'
import { useGoogleFontsIndex } from '@jobilla/nexus-sdk'
import { useCallback, useEffect, useState } from 'react'
import { loadFont } from '../font-loader'
import { buildVariantKey, parseVariantKey } from './variant-mapping'

export type GoogleFontMeta = GoogleFontsIndexResponse[number]

/**
 * Fetches the Google Fonts catalogue from the Nexus backend.
 * Thin wrapper around the auto-generated SDK hook with long stale time.
 */
export function useGoogleFontCatalogue(enabled: boolean = true) {
	return useGoogleFontsIndex({
		staleTime: 1000 * 60 * 60 * 24, // 24 hours — backend caches for 1 week
		retry: 1,
		enabled,
	})
}

export type UseLoadGoogleFontArgs = {
	family: string
	meta: GoogleFontMeta | undefined
	weight?: string | number
	style?: string
}

/**
 * Loads a specific Google Font variant. Returns loading/error state.
 * Only triggers when family and meta are provided.
 */
export function useLoadGoogleFont({ family, meta, weight = '400', style = 'normal' }: UseLoadGoogleFontArgs) {
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<Error | null>(null)

	useEffect(() => {
		if (!family || !meta) { return }

		const variantKey = buildVariantKey(weight, style)
		const fileUrl = meta.files[variantKey] ?? meta.files['regular'] ?? Object.values(meta.files)[0]
		if (!fileUrl) { return }

		const parsed = parseVariantKey(variantKey)

		setIsLoading(true)
		setError(null)

		loadFont(family, fileUrl, parsed.weight, parsed.style)
			.then(() => setIsLoading(false))
			.catch((err) => {
				setError(err instanceof Error ? err : new Error(String(err)))
				setIsLoading(false)
			})
	}, [family, meta, weight, style])

	return { isLoading, error }
}

export type UseLoadAllVariantsArgs = {
	family: string
	meta: GoogleFontMeta | undefined
}

/**
 * Loads ALL variants of a Google Font family (every weight + style).
 * This ensures any CSS font-weight/font-style used by child layers works.
 */
export function useLoadAllGoogleFontVariants({ family, meta }: UseLoadAllVariantsArgs) {
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<Error | null>(null)

	useEffect(() => {
		if (!family || !meta) { return }

		const entries = Object.entries(meta.files)
		if (entries.length === 0) { return }

		setIsLoading(true)
		setError(null)

		const loads = entries.map(([variantKey, fileUrl]) => {
			const { weight, style } = parseVariantKey(variantKey)
			return loadFont(family, fileUrl, weight, style)
		})

		Promise.all(loads)
			.then(() => setIsLoading(false))
			.catch((err) => {
				setError(err instanceof Error ? err : new Error(String(err)))
				setIsLoading(false)
			})
	}, [family, meta])

	return { isLoading, error }
}

/**
 * Returns a callback that loads all variants of a font and resolves when ready.
 * Useful for imperative loading (e.g. on font selection in a picker).
 */
export function useLoadGoogleFontCallback() {
	const { data: catalogue } = useGoogleFontCatalogue()

	return useCallback(async (family: string) => {
		if (!catalogue) { throw new Error('Font catalogue not loaded') }

		const meta = catalogue.find((f) => f.family === family)
		if (!meta) { throw new Error(`Font "${family}" not found in catalogue`) }

		const entries = Object.entries(meta.files)
		if (entries.length === 0) { throw new Error(`No font files for "${family}"`) }

		await Promise.all(entries.map(([variantKey, fileUrl]) => {
			const { weight, style } = parseVariantKey(variantKey)
			return loadFont(family, fileUrl, weight, style)
		}))
	}, [catalogue])
}
